import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import {
  acceptUserDuelChallenge,
  createUserDuelChallenge,
  declineUserDuelChallenge,
  getLeaderboard,
  getUserProfile,
  updateUserLocation,
} from '../api/dataService';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { Dialog, Tab, Transition } from '@headlessui/react';
import {
  formatDetailedCountdown,
  getDuelAvailabilityLabel,
  getDuelRecordLabel,
} from '../utils/duelUtils';
import { useLocale } from '../context/LocaleContext';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const FeaturedRivalryCard = ({ duelState, onOpenDuel, t }) => {
  const featured = duelState?.active || duelState?.incoming?.[0] || duelState?.outgoing?.[0] || null;
  if (!featured) return null;

  const opponentName = featured.otherUser?.username || t('leaderboard.opponent', 'Opponent');
  let subtitle = '';
  let ctaLabel = t('leaderboard.view', 'View');

  if (featured.status === 'active') {
    subtitle = t('leaderboard.youVs', 'You vs {opponent}', { opponent: opponentName });
    ctaLabel = t('leaderboard.openDuel', 'Open Duel');
  } else if (featured.direction === 'incoming') {
    subtitle = t('leaderboard.challengedYou', '{opponent} challenged you', { opponent: opponentName });
    ctaLabel = t('leaderboard.review', 'Review');
  } else {
    subtitle = t('leaderboard.youChallenged', 'You challenged {opponent}', { opponent: opponentName });
    ctaLabel = t('leaderboard.view', 'View');
  }

  const timeLabel = featured.status === 'active'
    ? t('leaderboard.timeLeft', '{time} left', { time: formatDetailedCountdown(featured.endsAt) })
    : t('leaderboard.expiresIn', 'Expires in {time}', { time: formatDetailedCountdown(featured.expiresAt) });

  return (
    <div className="mb-4 rounded-[1.5rem] border border-cyan-500/20 bg-slate-950/60 p-4 shadow-lg shadow-cyan-500/5">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-[0.24em] text-cyan-400">
            {t('leaderboard.currentRivalry', 'Current Rivalry')}
          </p>
          <p className="mt-2 text-base font-semibold text-white">{subtitle}</p>
          <p className="mt-1 text-sm text-slate-400">{timeLabel}</p>
        </div>
        <button
          type="button"
          onClick={onOpenDuel}
          className="inline-flex items-center justify-center rounded-xl border border-cyan-500/30 bg-cyan-500/10 px-4 py-2 text-sm font-semibold text-cyan-300 transition-colors hover:bg-cyan-500/20"
        >
          {ctaLabel}
        </button>
      </div>
    </div>
  );
};

const LeaderboardRow = ({ rankedUser, index, isCurrentUser, showAvailability, onSelect, availabilityCopy }) => {
  const availabilityLabel = showAvailability
    ? getDuelAvailabilityLabel(rankedUser.duelAvailability, new Date(), availabilityCopy)
    : '';
  const isInteractive = Boolean(!isCurrentUser && onSelect);

  return (
    <button
      type="button"
      disabled={!isInteractive}
      onClick={() => isInteractive && onSelect?.(rankedUser)}
      className={classNames(
        'w-full rounded-xl border p-3 text-left transition-colors',
        isCurrentUser
          ? 'bg-cyan-950/35 border-cyan-500/50 text-cyan-50'
          : 'bg-slate-950/55 border-white/5 text-gray-200 hover:bg-slate-900/80',
        isInteractive && 'cursor-pointer'
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-semibold text-sm sm:text-base break-words">#{index + 1} {rankedUser.username}</p>
          <p className="mt-1 text-[10px] uppercase tracking-[0.26em] text-slate-400">
            {rankedUser.league} - {rankedUser.tier}
          </p>
          {showAvailability && !isCurrentUser && availabilityLabel ? (
            <p className="mt-2 text-xs font-medium text-slate-400">{availabilityLabel}</p>
          ) : null}
        </div>
        <div className="flex flex-col items-end shrink-0">
          <p className="font-bold text-cyan-400 text-lg">{rankedUser.monthlyXp ?? 0} XP</p>
        </div>
      </div>
    </button>
  );
};

const DuelHeroCard = ({ duel, t }) => {
  if (!duel) return null;
  const currentScore = duel.currentUserScore || 0;
  const otherScore = duel.otherUserScore || 0;
  const maxScore = Math.max(currentScore, otherScore, 1);
  const currentWidth = `${Math.max(8, (currentScore / maxScore) * 100)}%`;
  const otherWidth = `${Math.max(8, (otherScore / maxScore) * 100)}%`;
  const lead = currentScore - otherScore;
  const opponentName = duel.otherUser?.username || t('leaderboard.opponent', 'Opponent');
  const winReward = t('leaderboard.winRewardValue', '750 Auras + 200 XP');

  return (
    <div className="rounded-[1.75rem] border border-cyan-500/20 bg-slate-950/60 p-5 shadow-lg shadow-cyan-500/5">
      <p className="text-xs font-semibold uppercase tracking-[0.24em] text-cyan-400">
        {t('leaderboard.currentRivalry', 'Current Rivalry')}
      </p>
      <div className="mt-3 flex items-center justify-between gap-3">
        <div>
          <p className="text-xl font-semibold text-white">
            {t('leaderboard.youVs', 'You vs {opponent}', { opponent: opponentName })}
          </p>
          <p className="mt-1 text-sm text-slate-400">
            {t('leaderboard.timeLeft', '{time} left', { time: formatDetailedCountdown(duel.endsAt) })}
          </p>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-2 gap-4">
        <div>
          <p className="text-sm text-slate-400">{t('leaderboard.you', 'You')}</p>
          <p className="mt-1 text-2xl font-bold text-cyan-400">{currentScore} XP</p>
          <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-800">
            <div className="h-full rounded-full bg-cyan-400" style={{ width: currentWidth }} />
          </div>
        </div>
        <div className="text-right">
          <p className="text-sm text-slate-400">{opponentName}</p>
          <p className="mt-1 text-2xl font-bold text-cyan-400">{otherScore} XP</p>
          <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-800">
            <div className="ml-auto h-full rounded-full bg-cyan-300/80" style={{ width: otherWidth }} />
          </div>
        </div>
      </div>

      <div className="mt-5 flex items-center justify-between gap-3 text-sm">
        <p className="text-slate-400">
          {t('leaderboard.lead', 'Lead: {value} XP', { value: lead >= 0 ? `+${lead}` : lead })}
        </p>
        <p className="text-slate-400">
          {t('leaderboard.rewardOnWin', 'Reward on win: {reward}', { reward: winReward })}
        </p>
      </div>
    </div>
  );
};

const DuelRequestList = ({ title, requests, onAccept, onDecline, t }) => {
  if (!requests?.length) return null;

  return (
    <div className="rounded-[1.5rem] border border-white/10 bg-slate-950/55 p-4">
      <p className="text-sm font-semibold text-white">{title}</p>
      <div className="mt-3 space-y-3">
        {requests.map((request) => (
          <div key={request.id} className="rounded-xl border border-white/5 bg-slate-900/70 p-3">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-white">{request.otherUser?.username}</p>
                <p className="mt-1 text-xs uppercase tracking-[0.2em] text-slate-400">
                  {request.otherUser?.league} - {request.otherUser?.tier}
                </p>
                <p className="mt-2 text-xs text-slate-400">
                  {request.direction === 'incoming'
                    ? t('leaderboard.expiresIn', 'Expires in {time}', { time: formatDetailedCountdown(request.expiresAt) })
                    : t('leaderboard.sentExpiresIn', 'Sent · expires in {time}', { time: formatDetailedCountdown(request.expiresAt) })}
                </p>
              </div>
              <p className="text-xs font-medium text-slate-400">{getDuelRecordLabel(request.recentRecord)}</p>
            </div>

            {request.direction === 'incoming' ? (
              <div className="mt-4 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => onDecline?.(request)}
                  className="rounded-xl border border-white/10 bg-slate-800/70 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-slate-700/80"
                >
                  {t('leaderboard.decline', 'Decline')}
                </button>
                <button
                  type="button"
                  onClick={() => onAccept?.(request)}
                  className="rounded-xl bg-cyan-500 px-4 py-2 text-sm font-semibold text-slate-950 transition-colors hover:bg-cyan-400"
                >
                  {t('leaderboard.acceptChallenge', 'Accept Challenge')}
                </button>
              </div>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
};

const DuelHistoryList = ({ history, t }) => {
  if (!history?.length) return null;

  return (
    <div className="rounded-[1.5rem] border border-white/10 bg-slate-950/55 p-4">
      <p className="text-sm font-semibold text-white">{t('leaderboard.recentHistory', 'Last 5')}</p>
      <div className="mt-3 space-y-3">
        {history.map((entry) => {
          const label = entry.outcome === 'winner'
            ? t('leaderboard.win', 'Win')
            : entry.outcome === 'loser'
              ? t('leaderboard.loss', 'Loss')
              : t('leaderboard.tie', 'Tie');
          const reward = entry.reward ? `+${entry.reward.auras}A +${entry.reward.xp}XP` : '';
          return (
            <div key={entry.id} className="flex items-center justify-between gap-3 rounded-xl border border-white/5 bg-slate-900/70 px-3 py-3">
              <div>
                <p className="text-sm font-semibold text-white">
                  {t('leaderboard.resultVs', '{result} vs {opponent}', {
                    result: label,
                    opponent: entry.otherUser?.username || t('leaderboard.opponent', 'Opponent'),
                  })}
                </p>
                <p className="mt-1 text-xs uppercase tracking-[0.2em] text-slate-400">{entry.otherUser?.league} - {entry.otherUser?.tier}</p>
              </div>
              <p className="text-xs font-semibold text-cyan-300">{reward}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const OpponentModal = ({
  isOpen,
  onClose,
  opponent,
  duelState,
  onSendChallenge,
  onAcceptChallenge,
  onDeclineChallenge,
  isSubmitting,
  t,
  availabilityCopy,
}) => {
  if (!opponent) return null;

  const duelAvailability = opponent.duelAvailability || {};
  const recordLabel = getDuelRecordLabel(duelAvailability.recentRecord);
  const hasActiveDuel = Boolean(duelState?.constraints?.hasActiveDuel);
  const hasOutgoingLimit = (duelState?.constraints?.outgoingPendingCount || 0) >= (duelState?.constraints?.maxOutgoingPending || 3);

  let primaryLabel = t('leaderboard.sendChallenge', 'Send Challenge');
  let primaryDisabled = false;
  let helperText = t('leaderboard.helperDefault', '7-day duel. Score = all XP earned during the challenge.');
  const availabilityLabel = getDuelAvailabilityLabel(duelAvailability, new Date(), availabilityCopy) || t('leaderboard.available', 'Available');
  const winReward = t('leaderboard.winRewardValue', '750 Auras + 200 XP');
  const tieReward = t('leaderboard.tieRewardValue', '375 Auras + 100 XP each');

  if (duelAvailability.state === 'active') {
    primaryLabel = t('leaderboard.unavailable', 'Unavailable');
    primaryDisabled = true;
    helperText = t(
      'leaderboard.helperActiveDuelUser',
      'This user is already in a duel. Time remaining: {time}.',
      { time: formatDetailedCountdown(duelAvailability.activeDuelEndsAt) }
    );
  } else if (duelAvailability.state === 'pending') {
    primaryLabel = duelAvailability.pendingDirection === 'incoming'
      ? t('leaderboard.acceptChallenge', 'Accept Challenge')
      : t('leaderboard.pending', 'Pending');
    primaryDisabled = duelAvailability.pendingDirection !== 'incoming';
    helperText = duelAvailability.pendingDirection === 'incoming'
      ? t('leaderboard.helperPendingIncoming', 'This user already challenged you.')
      : t('leaderboard.helperPendingOutgoing', 'You already have a pending challenge with this user.');
  } else if (hasActiveDuel) {
    primaryLabel = t('leaderboard.unavailable', 'Unavailable');
    primaryDisabled = true;
    helperText = t('leaderboard.helperActiveDuelSelf', 'You already have an active duel. Finish it before sending a new challenge.');
  } else if (hasOutgoingLimit) {
    primaryLabel = t('leaderboard.limitReached', 'Limit Reached');
    primaryDisabled = true;
    helperText = t('leaderboard.helperOutgoingLimit', 'You already have 3 pending outgoing challenges.');
  }

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-20" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-[2rem] border border-white/10 bg-slate-900/90 p-6 text-left align-middle shadow-2xl transition-all">
                <Dialog.Title as="h3" className="text-2xl font-bold text-white">
                  {opponent.username}
                </Dialog.Title>
                <div className="mt-2 space-y-1 text-sm text-slate-300">
                  <p>{t('leaderboard.level', 'Level {level}', { level: opponent.level })}</p>
                  <p className="uppercase tracking-[0.18em] text-slate-400">{opponent.league} - {opponent.tier}</p>
                  <p>{t('leaderboard.monthlyXp', 'Monthly XP: {xp}', { xp: opponent.monthlyXp ?? 0 })}</p>
                  <p>{t('leaderboard.lastFive', 'Last 5: {record}', { record: recordLabel })}</p>
                  <p>{t('leaderboard.status', 'Status: {status}', { status: availabilityLabel })}</p>
                </div>

                <div className="mt-5 rounded-2xl border border-white/5 bg-slate-950/60 p-4 text-sm text-slate-300">
                  <p>{helperText}</p>
                  <p className="mt-3">{t('leaderboard.winnerReward', 'Winner: {reward}', { reward: winReward })}</p>
                  <p className="mt-1">{t('leaderboard.tieReward', 'Tie: {reward}', { reward: tieReward })}</p>
                </div>

                <div className="mt-6 flex flex-wrap justify-end gap-3">
                  {duelAvailability.pendingDirection === 'incoming' ? (
                    <button
                      type="button"
                      disabled={isSubmitting}
                      onClick={() => onDeclineChallenge?.(duelAvailability.pendingDuelId)}
                      className="inline-flex items-center justify-center rounded-xl border border-white/15 bg-slate-800/80 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-slate-700/80"
                    >
                      {t('leaderboard.decline', 'Decline')}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={onClose}
                      className="inline-flex items-center justify-center rounded-xl border border-white/15 bg-slate-800/80 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-slate-700/80"
                    >
                      {t('leaderboard.cancel', 'Cancel')}
                    </button>
                  )}

                  <button
                    type="button"
                    disabled={primaryDisabled || isSubmitting}
                    onClick={() => {
                      if (duelAvailability.pendingDirection === 'incoming') {
                        onAcceptChallenge?.(duelAvailability.pendingDuelId);
                        return;
                      }
                      onSendChallenge?.(opponent._id);
                    }}
                    className={classNames(
                      'inline-flex items-center justify-center rounded-xl px-5 py-2.5 text-sm font-semibold transition-colors',
                      primaryDisabled || isSubmitting
                        ? 'cursor-not-allowed bg-slate-700/60 text-slate-400'
                        : 'bg-cyan-500 text-slate-950 hover:bg-cyan-400'
                    )}
                  >
                    {isSubmitting ? t('leaderboard.working', 'Working...') : primaryLabel}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

const LeaderboardPage = () => {
  const [leaderboard, setLeaderboard] = useState(null);
  const [filter, setFilter] = useState('global');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [userProfile, setUserProfile] = useState(null);
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const [newUserLocation, setNewUserLocation] = useState('');
  const [modalError, setModalError] = useState('');
  const [selectedOpponent, setSelectedOpponent] = useState(null);
  const [isDuelActionPending, setIsDuelActionPending] = useState(false);

  const { user } = useAuth();
  const { addToast } = useToast();
  const { t } = useLocale();
  const availabilityCopy = useMemo(() => ({
    pending: t('leaderboard.pending', 'Pending'),
    inDuel: t('leaderboard.inDuel', 'In Duel'),
    available: t('leaderboard.available', 'Available'),
  }), [t]);

  const availableTabs = useMemo(() => {
    const tabs = [
      { key: 'global', label: t('leaderboard.tabs.global', 'Global') },
      { key: 'regional', label: t('leaderboard.tabs.regional', 'Regional') },
      { key: 'league', label: t('leaderboard.tabs.myLeague', 'My League') },
    ];
    if (leaderboard?.duelState?.tabVisible) {
      tabs.push({ key: 'duel', label: t('leaderboard.tabs.duel', 'Duel') });
    }
    return tabs;
  }, [leaderboard?.duelState?.tabVisible, t]);

  const fetchInitialData = useCallback(async (activeFilter) => {
    if (!user) return;
    setIsLoading(true);
    setError('');
    try {
      const [profileRes, leaderboardRes] = await Promise.all([
        getUserProfile(),
        getLeaderboard(activeFilter),
      ]);
      setUserProfile(profileRes.data);
      setLeaderboard(leaderboardRes.data);
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.error || t('leaderboard.loadFailed', 'Could not load data. Please try again later.'));
    } finally {
      setIsLoading(false);
    }
  }, [t, user]);

  useEffect(() => {
    fetchInitialData(filter);
  }, [fetchInitialData, filter]);

  useEffect(() => {
    if (filter === 'duel' && !leaderboard?.duelState?.tabVisible) {
      setFilter('league');
    }
  }, [filter, leaderboard?.duelState?.tabVisible]);

  useEffect(() => {
    if (!leaderboard?.duelState?.active || !user) return undefined;
    const intervalId = window.setInterval(() => {
      fetchInitialData(filter);
    }, 30000);
    return () => window.clearInterval(intervalId);
  }, [fetchInitialData, filter, leaderboard?.duelState?.active, user]);

  const selectedIndex = Math.max(availableTabs.findIndex((tab) => tab.key === filter), 0);

  const handleFilterChange = (index) => {
    const nextFilter = availableTabs[index]?.key;
    if (!nextFilter) return;
    if (nextFilter === 'regional' && !userProfile?.profileData?.profile?.personalInformation?.location) {
      setIsLocationModalOpen(true);
      return;
    }
    setFilter(nextFilter);
  };

  const handleLocationSave = async () => {
    if (!newUserLocation.trim()) {
      setModalError(t('leaderboard.locationInvalid', 'Please enter a valid country.'));
      return;
    }
    try {
      const res = await updateUserLocation(newUserLocation);
      setUserProfile(res.data.user);
      setIsLocationModalOpen(false);
      setFilter('regional');
      setModalError('');
      addToast(t('leaderboard.savedToast', 'Location saved'), 'success');
    } catch (err) {
      setModalError(t('leaderboard.locationSaveFailed', 'Failed to save location.'));
    }
  };

  const handleOpenDuelTab = () => {
    if (leaderboard?.duelState?.tabVisible) {
      setFilter('duel');
    }
  };

  const handleSendChallenge = useCallback(async (opponentUserId) => {
    try {
      setIsDuelActionPending(true);
      await createUserDuelChallenge(opponentUserId);
      addToast(t('leaderboard.challengeSent', 'Challenge sent'), 'success');
      setSelectedOpponent(null);
      await fetchInitialData(filter === 'duel' ? 'league' : filter);
    } catch (err) {
      addToast(err?.response?.data?.error || t('leaderboard.sendChallengeFailed', 'Failed to send challenge.'), 'error');
    } finally {
      setIsDuelActionPending(false);
    }
  }, [addToast, fetchInitialData, filter, t]);

  const handleAcceptChallenge = useCallback(async (duelId) => {
    try {
      setIsDuelActionPending(true);
      await acceptUserDuelChallenge(duelId);
      addToast(t('leaderboard.challengeAccepted', 'Challenge accepted'), 'success');
      setSelectedOpponent(null);
      setFilter('duel');
      await fetchInitialData('duel');
    } catch (err) {
      addToast(err?.response?.data?.error || t('leaderboard.acceptChallengeFailed', 'Failed to accept challenge.'), 'error');
    } finally {
      setIsDuelActionPending(false);
    }
  }, [addToast, fetchInitialData, t]);

  const handleDeclineChallenge = useCallback(async (duelId) => {
    try {
      setIsDuelActionPending(true);
      await declineUserDuelChallenge(duelId);
      addToast(t('leaderboard.challengeDeclined', 'Challenge declined'), 'success');
      setSelectedOpponent(null);
      await fetchInitialData(filter === 'duel' ? 'league' : filter);
    } catch (err) {
      addToast(err?.response?.data?.error || t('leaderboard.declineChallengeFailed', 'Failed to decline challenge.'), 'error');
    } finally {
      setIsDuelActionPending(false);
    }
  }, [addToast, fetchInitialData, filter, t]);

  const renderRankingsPanel = (tabKey, showAvailability = false) => {
    if (isLoading) {
      return <div className="flex justify-center items-center h-full pt-10 text-sm text-slate-400">{t('leaderboard.loading', 'Loading leaderboard...')}</div>;
    }
    if (error) {
      return <div className="bg-red-900/50 text-red-300 p-3 rounded-xl border border-red-500/30 text-center">{error}</div>;
    }
    if (!leaderboard || !user) return null;
    if (!leaderboard.rankings?.length) {
      return (
        <div className="text-center text-slate-400 py-10">
          <p className="text-base font-semibold text-white">{t('leaderboard.noData', 'No leaderboard data')}</p>
        </div>
      );
    }

    return (
      <div>
        {tabKey === 'league' && leaderboard.duelState ? (
          <FeaturedRivalryCard duelState={leaderboard.duelState} onOpenDuel={handleOpenDuelTab} t={t} />
        ) : null}

        <h3 className="text-lg font-medium text-center text-white mb-1">
          {tabKey === 'global' && t('leaderboard.globalRankings', 'Global Rankings')}
          {tabKey === 'regional' && userProfile?.profileData?.profile?.personalInformation?.location && (
            t('leaderboard.locationTitle', 'Location: {location}', {
              location: userProfile.profileData.profile.personalInformation.location.charAt(0).toUpperCase()
                + userProfile.profileData.profile.personalInformation.location.slice(1).toLowerCase(),
            })
          )}
          {tabKey === 'league' && `${leaderboard.currentUserRanking?.league || t('leaderboard.leagueFallback', 'League')} - ${leaderboard.currentUserRanking?.tier || t('leaderboard.tierFallback', 'Tier')}`}
        </h3>
        <h4 className="text-md font-medium text-center text-gray-400 mb-4">
          {t('leaderboard.myRank', 'Your Rank: {rank} / {total}', {
            rank: leaderboard.userRank,
            total: leaderboard.totalPlayers,
          })}
        </h4>

        <div className="space-y-3 max-h-[55vh] overflow-y-auto pr-2">
          {leaderboard.rankings.map((rankedUser, index) => (
            <LeaderboardRow
              key={rankedUser._id}
              rankedUser={rankedUser}
              index={index}
              isCurrentUser={rankedUser._id === user.id || rankedUser._id === user?._id}
              showAvailability={showAvailability}
              onSelect={showAvailability ? setSelectedOpponent : undefined}
              availabilityCopy={availabilityCopy}
            />
          ))}
        </div>
      </div>
    );
  };

  const renderDuelPanel = () => {
    const duelState = leaderboard?.duelState;
    if (isLoading) {
      return <div className="flex justify-center items-center h-full pt-10 text-sm text-slate-400">{t('leaderboard.loadingDuel', 'Loading duel state...')}</div>;
    }
    if (error) {
      return <div className="bg-red-900/50 text-red-300 p-3 rounded-xl border border-red-500/30 text-center">{error}</div>;
    }
    if (!duelState) return null;

    return (
      <div className="space-y-4">
        {duelState.active ? <DuelHeroCard duel={duelState.active} t={t} /> : null}
        <DuelRequestList
          title={t('leaderboard.incomingChallenges', 'Incoming Challenges')}
          requests={duelState.incoming}
          onAccept={(request) => handleAcceptChallenge(request.id)}
          onDecline={(request) => handleDeclineChallenge(request.id)}
          t={t}
        />
        <DuelRequestList title={t('leaderboard.outgoingChallenges', 'Outgoing Challenges')} requests={duelState.outgoing} t={t} />
        <DuelHistoryList history={duelState.history} t={t} />
        {!duelState.active && !duelState.incoming?.length && !duelState.outgoing?.length ? (
          <div className="rounded-[1.5rem] border border-white/10 bg-slate-950/55 p-5 text-center text-slate-400">
            {t('leaderboard.noActiveOrPendingDuels', 'No active or pending duels.')}
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div className="min-h-screen text-gray-300 font-sans">
      <div className="container mx-auto px-4 max-w-md">
        <h1 className="text-3xl font-bold text-white my-4">{t('leaderboard.pageTitle', 'Leaderboard')}</h1>

        <div className="w-full">
          <Tab.Group selectedIndex={selectedIndex} onChange={handleFilterChange}>
            <Tab.List className="flex space-x-1 rounded-xl bg-slate-950/50 border border-white/5 shadow-inner p-1">
              {availableTabs.map((tab) => (
                <Tab
                  key={tab.key}
                  className={({ selected }) =>
                    classNames(
                      'w-full rounded-lg py-2.5 text-sm font-medium leading-5 transition-all duration-200',
                      'focus:outline-none focus:ring-2 focus:ring-cyan-400',
                      selected
                        ? 'bg-slate-800 text-cyan-400 shadow border border-white/10'
                        : 'text-gray-400 hover:text-gray-200 hover:bg-slate-800/50'
                    )
                  }
                >
                  {tab.label}
                </Tab>
              ))}
            </Tab.List>
            <Tab.Panels className="mt-4 bg-slate-900/40 border border-white/10 backdrop-blur-md rounded-[1.5rem] p-5 shadow-xl min-h-[300px]">
              {availableTabs.map((tab) => (
                <Tab.Panel key={tab.key}>
                  {tab.key === 'duel'
                    ? renderDuelPanel()
                    : renderRankingsPanel(tab.key, tab.key === 'league')}
                </Tab.Panel>
              ))}
            </Tab.Panels>
          </Tab.Group>
        </div>
      </div>

      <Transition appear show={isLocationModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={() => setIsLocationModalOpen(false)}>
          <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
            <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-[2rem] bg-slate-900/60 border border-white/10 backdrop-blur-2xl p-6 text-left align-middle shadow-2xl transition-all">
                  <Dialog.Title as="h3" className="text-lg font-bold leading-6 text-white">
                    {t('leaderboard.locationModalTitle', 'Set Your Location')}
                  </Dialog.Title>
                  <div className="mt-2">
                    <p className="text-sm text-gray-400">
                      {t('leaderboard.locationModalBody', 'To view the regional leaderboard, please provide your country.')}
                    </p>
                  </div>

                  <div className="mt-4">
                    <input
                      type="text"
                      value={newUserLocation}
                      onChange={(e) => setNewUserLocation(e.target.value)}
                      placeholder={t('leaderboard.locationPlaceholder', 'e.g. Canada')}
                      className="w-full bg-slate-950/50 border border-white/10 shadow-inner rounded-xl py-3 px-4 text-white placeholder-gray-500 focus:outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 transition-all"
                    />
                    {modalError && <p className="text-sm text-red-400 mt-2">{modalError}</p>}
                  </div>

                  <div className="mt-6 flex justify-end gap-3">
                    <button type="button" onClick={() => setIsLocationModalOpen(false)} className="inline-flex items-center justify-center rounded-xl border border-white/20 bg-slate-800/50 px-5 py-2.5 text-sm font-bold tracking-wide text-white transition-all duration-200 hover:bg-slate-700/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400">
                      {t('leaderboard.cancel', 'Cancel')}
                    </button>
                    <button type="button" onClick={handleLocationSave} className="inline-flex items-center justify-center rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 px-6 py-2.5 text-sm font-bold tracking-wide text-white shadow-lg transition-all duration-200 hover:from-cyan-400 hover:to-blue-500 hover:shadow-cyan-500/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400">
                      {t('leaderboard.save', 'Save')}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      <OpponentModal
        isOpen={Boolean(selectedOpponent)}
        onClose={() => setSelectedOpponent(null)}
        opponent={selectedOpponent}
        duelState={leaderboard?.duelState}
        onSendChallenge={handleSendChallenge}
        onAcceptChallenge={handleAcceptChallenge}
        onDeclineChallenge={handleDeclineChallenge}
        isSubmitting={isDuelActionPending}
        t={t}
        availabilityCopy={availabilityCopy}
      />
    </div>
  );
};

export default LeaderboardPage;
